import { pusherStore } from '~/common/contexts/pusherStore';
import { PusherEventType } from '~/enums/PusherEventType';
import { api } from '~/redux/api/createApi';
import { TagType } from '~/redux/api/TagType';
import { logger } from '~/utils/logger';

type ExtractionEventType = 'CLAUSE_EXTRACTED' | 'FIELD_EXTRACTED';

type ExtractionUpdatedEvent = {
  document_id: number;
  event_type: ExtractionEventType;
};

type SubscribeToDocumentExtractionsArg = {
  pilotDocumentId: string;
  documentTag?: string;
};

const DEBOUNCE_MS = 500;

/**
 * RTK Query `onCacheEntryAdded` handler for real-time document extraction
 * updates via Pusher.
 *
 * Subscribes to `private-pilot-doc-{pilotDocumentId}` when the cache entry is
 * created. On each `extraction-updated` event the relevant CLMe field/clause
 * tags are invalidated (debounced at 500 ms to coalesce rapid-fire extraction
 * batches). The channel is unsubscribed when the cache entry is removed.
 */
export const onDocumentExtractionCacheEntryAdded = async (
  arg: SubscribeToDocumentExtractionsArg,
  {
    cacheDataLoaded,
    cacheEntryRemoved,
    dispatch,
  }: {
    cacheDataLoaded: Promise<{ data: null; meta: unknown }>;
    cacheEntryRemoved: Promise<void>;
    dispatch: (action: ReturnType<typeof api.util.invalidateTags>) => void;
  },
) => {
  await cacheDataLoaded;

  const channelName = `private-pilot-doc-${arg.pilotDocumentId}`;
  const pusher = pusherStore.getPusher();
  const channel = pusherStore.subscribe(pusher, channelName, {
    success: () => {
      logger.pusher('subscribed %s', channelName);
    },
  });

  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let pendingTags = new Set<string>();

  const flushInvalidation = () => {
    if (!arg.documentTag) {
      pendingTags = new Set();
      return;
    }

    const tags = [
      ...(pendingTags.has('fields')
        ? [{ type: TagType.CLMeFields as const, id: arg.documentTag }]
        : []),
      ...(pendingTags.has('clauses')
        ? [{ type: TagType.CLMeClauses as const, id: arg.documentTag }]
        : []),
    ];

    if (tags.length > 0) {
      logger.pusher('%s → flushing invalidation %o', channelName, tags);
      dispatch(api.util.invalidateTags(tags));
    }
    pendingTags = new Set();
  };

  channel.bind(
    PusherEventType.ExtractionUpdated,
    (event: ExtractionUpdatedEvent) => {
      logger.pusher(
        '%s → %s %o',
        channelName,
        PusherEventType.ExtractionUpdated,
        event,
      );

      if (event.event_type === 'FIELD_EXTRACTED') {
        pendingTags.add('fields');
      } else if (event.event_type === 'CLAUSE_EXTRACTED') {
        pendingTags.add('clauses');
      }

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(flushInvalidation, DEBOUNCE_MS);
    },
  );

  await cacheEntryRemoved;
  clearTimeout(debounceTimer);
  logger.pusher('unsubscribed %s', channelName);
  pusher.unsubscribe(channelName);
};
